/**
 * SaveAvatarModal
 *
 * Redesigned modal for saving generated avatar images.
 * Supports dark/light themes with selected images preview,
 * folder selection, and default avatar options.
 */

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { FolderOpen, Settings, Save, Loader2, X } from 'lucide-react';
import { AVATAR_CONSTANTS, type ISaveAvatarModalProps } from '@/@types/avatars';
import type { Id } from '../../../convex/_generated/dataModel';

export const SaveAvatarModal = ({
  isOpen,
  onClose,
  onSave,
  isSaving,
  selectedImages,
  folders,
  selectedFolderId,
  onFolderChange,
  newFolderTitle,
  onNewFolderTitleChange,
  isDefault,
  onDefaultChange,
  referencePersonName,
}: ISaveAvatarModalProps) => {
  const count = selectedImages.length;
  const isCreateNewFolder = !selectedFolderId;
  const canSave = selectedFolderId || newFolderTitle.trim();

  const handleFolderChange = (value: string) => {
    if (value === 'new') {
      onFolderChange(null);
    } else {
      onFolderChange(value as Id<'avatarFolders'>);
      onNewFolderTitleChange('');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent className="sm:max-w-md" showCloseButton={false}>
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-lg font-semibold">
                Save {count} {count === 1 ? 'Avatar' : 'Avatars'}
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground mt-1">
                Save to your avatar library for future use
              </DialogDescription>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              disabled={isSaving}
              className="h-8 w-8 -mr-2 -mt-2"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Selected Images Preview */}
          <div className="space-y-2">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Selected Images
            </span>
            <div className="flex gap-3 flex-wrap">
              {selectedImages.map(image => (
                <div
                  key={image.storageId}
                  className="w-16 h-16 rounded-lg overflow-hidden ring-2 ring-primary ring-offset-2 ring-offset-background flex-shrink-0"
                >
                  <img src={image.url} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>

          {/* Save to Folder — only show dropdown when folders exist */}
          {folders.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <FolderOpen className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium">Save to Folder</span>
              </div>
              <Select
                value={selectedFolderId || 'new'}
                onValueChange={handleFolderChange}
                disabled={isSaving}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a folder" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">+ Create new folder</SelectItem>
                  {folders.map(folder => (
                    <SelectItem key={folder._id} value={folder._id}>
                      {folder.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* New Folder Name (conditional) */}
          {isCreateNewFolder && (
            <div className="space-y-2">
              <span className="text-sm font-medium">New Folder Name</span>
              <Input
                value={newFolderTitle}
                onChange={e => onNewFolderTitleChange(e.target.value)}
                placeholder="Enter folder name"
                maxLength={AVATAR_CONSTANTS.MAX_FOLDER_TITLE_LENGTH}
                disabled={isSaving}
              />
            </div>
          )}

          {/* Options */}
          {referencePersonName && onDefaultChange && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Settings className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium">Options</span>
              </div>
              <label
                htmlFor="save-avatar-set-default"
                className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 cursor-pointer hover:bg-muted/70 transition-colors"
              >
                <Checkbox
                  id="save-avatar-set-default"
                  checked={isDefault ?? false}
                  onCheckedChange={checked => onDefaultChange(checked === true)}
                  disabled={isSaving}
                  className="mt-0.5"
                />
                <div className="space-y-1">
                  <span className="text-sm font-medium leading-none">
                    Set as default for {referencePersonName || 'this person'}
                  </span>
                  <p className="text-xs text-muted-foreground">
                    Use this avatar by default when creating content
                  </p>
                </div>
              </label>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={isSaving || !canSave}>
            {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
            <Save className="w-4 h-4" />
            Save {count === 1 ? 'Avatar' : 'Avatars'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
